import { Channel } from "pusher-js";

import { getSessionId } from "../../utils/sessionId";
import { getPusher } from "../pusher/pusherConnection";
import { SearchProgressStore } from "./searchProgressStore";
import { SearchProgressEvent } from "./types";

// Search-progress channel — its OWN Pusher channel (private-search-${sessionId})
// on the SHARED connection. Routes streamed "search-progress" events into
// SearchProgressStore. Fully independent of intent/Tiresias. Connection
// lifecycle is owned by <PusherBootstrap/>.
let channel: Channel | null = null;

const handleSearchProgress = (data: unknown): void => {
  // Receipt log: confirms a REAL Pusher event reached the bound handler (the dev
  // feeder bypasses this and calls the store directly). Logs the raw shape so a
  // string-vs-object payload mismatch from the publisher / Debug Console is
  // obvious — `typeof data` "object" with a `.type` string is what we want.
  console.log(`[search] event received type=${typeof data}`, data);
  // Tolerate a JSON-string payload: pusher-js normally hands back a parsed
  // object, but some publishers / the Debug Console can deliver the data
  // pre-stringified. Parse it rather than silently skip.
  let parsed: unknown = data;
  if (typeof data === "string") {
    try {
      parsed = JSON.parse(data);
    } catch {
      console.warn(`[search] event data is a non-JSON string — skipped`, data);
      return;
    }
  }
  const evt = parsed as SearchProgressEvent;
  if (evt && typeof evt.type === "string") {
    SearchProgressStore.apply(evt);
  } else {
    console.warn(`[search] search-progress event missing type — skipped`, data);
  }
};

export function subscribeSearchProgressChannel(): void {
  const pusher = getPusher();
  if (!pusher || channel) return;
  const channelName = `private-search-${getSessionId()}`;
  channel = pusher.subscribe(channelName);
  channel.bind("pusher:subscription_succeeded", () => {
    console.log(`[search] subscribed channel=${channelName}`);
  });
  // Private-channel auth failure surfaces here (e.g. the auth endpoint doesn't
  // authorize private-search-*). Without this it fails silently.
  channel.bind("pusher:subscription_error", (err: unknown) => {
    console.warn(`[search] subscription_error channel=${channelName}`, err);
  });
  channel.bind("search-progress", handleSearchProgress);
}

export function unsubscribeSearchProgressChannel(): void {
  if (!channel) return;
  channel.unbind_all();
  getPusher()?.unsubscribe(channel.name);
  channel = null;
}
